import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ArrowRight, Repeat, CalendarDays, CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import { DayCheckboxGroup } from '../../DayCheckboxGroup';
import { RecurrenceSelector, type RecurrenceType } from '../../RecurrenceSelector';
import { EndDateSelector, type EndType } from '../../EndDateSelector';
import { ModeCard } from '../components';
import { endTimeFromStart } from '@/utils/time';

export type GroupRecurrenceMode = 'recurring' | 'one_time';

interface GroupRecurrenceStepProps {
  mode: GroupRecurrenceMode;
  onModeChange: (mode: GroupRecurrenceMode) => void;

  // Recurring
  recurrenceType: RecurrenceType;
  onRecurrenceTypeChange: (t: RecurrenceType) => void;
  recurrenceInterval: number;
  onRecurrenceIntervalChange: (n: number) => void;
  monthlyWeek: number | null;
  onMonthlyWeekChange: (w: number) => void;
  monthlyDay: number | null;
  onMonthlyDayChange: (d: number) => void;
  selectedDays: number[];
  onSelectedDaysChange: (days: number[]) => void;
  endType: EndType;
  onEndTypeChange: (t: EndType) => void;
  endDate: Date | null;
  onEndDateChange: (d: Date | null) => void;
  occurrences: number | null;
  onOccurrencesChange: (n: number) => void;

  // One-time
  oneTimeDate?: Date | null;
  onOneTimeDateChange?: (d: Date | null) => void;

  // Shared
  startTime: string;
  onStartTimeChange: (t: string) => void;
  durationMinutes: number;
  serviceName: string;
}

export function GroupRecurrenceStep({
  mode,
  onModeChange,
  recurrenceType,
  onRecurrenceTypeChange,
  recurrenceInterval,
  onRecurrenceIntervalChange,
  monthlyWeek,
  onMonthlyWeekChange,
  monthlyDay,
  onMonthlyDayChange,
  selectedDays,
  onSelectedDaysChange,
  endType,
  onEndTypeChange,
  endDate,
  onEndDateChange,
  occurrences,
  onOccurrencesChange,
  oneTimeDate,
  onOneTimeDateChange,
  startTime,
  onStartTimeChange,
  durationMinutes,
  serviceName,
}: GroupRecurrenceStepProps) {
  const { t } = useTranslation('staff');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const computedEnd = endTimeFromStart(startTime, durationMinutes);

  return (
    <div className="p-6 space-y-5">
      <div className="space-y-2">
        <Label variant="section">
          {t('availability.groupClass.recurrence.title', 'When does this class run?')}
        </Label>
        <ModeCard
          icon={Repeat}
          title={t('availability.groupClass.recurrence.subModeRecurring', 'Recurring')}
          description={t(
            'availability.groupClass.recurrence.subModeRecurringDesc',
            'Repeats weekly, biweekly, or monthly until an end condition.'
          )}
          selected={mode === 'recurring'}
          onSelect={() => onModeChange('recurring')}
        />
        <ModeCard
          icon={CalendarDays}
          title={t('availability.groupClass.recurrence.subModeOneTime', 'One-off')}
          description={t(
            'availability.groupClass.recurrence.subModeOneTimeDesc',
            'A single class on a specific date.'
          )}
          selected={mode === 'one_time'}
          onSelect={() => onModeChange('one_time')}
        />
      </div>

      {mode === 'recurring' && (
        <>
          <div className="space-y-3">
            <Label variant="section">
              {t('availability.recurrence.repeats', 'Repeats')}
            </Label>
            <RecurrenceSelector
              recurrenceType={recurrenceType}
              onRecurrenceTypeChange={onRecurrenceTypeChange}
              recurrenceInterval={recurrenceInterval}
              onRecurrenceIntervalChange={onRecurrenceIntervalChange}
              monthlyWeek={monthlyWeek}
              onMonthlyWeekChange={onMonthlyWeekChange}
              monthlyDay={monthlyDay}
              onMonthlyDayChange={onMonthlyDayChange}
            />
          </div>

          {recurrenceType !== 'monthly' && (
            <div className="space-y-3">
              <Label variant="section">
                {t('availability.dialog.days', 'Days')}
              </Label>
              <DayCheckboxGroup
                value={selectedDays}
                onChange={onSelectedDaysChange}
                showPresets={false}
                showSummary={false}
              />
            </div>
          )}

          <div className="space-y-3">
            <Label variant="section">
              {t('availability.ends.label', 'Ends')}
            </Label>
            <EndDateSelector
              endType={endType}
              onEndTypeChange={onEndTypeChange}
              endDate={endDate}
              onEndDateChange={onEndDateChange}
              occurrences={occurrences}
              onOccurrencesChange={onOccurrencesChange}
            />
          </div>
        </>
      )}

      {mode === 'one_time' && onOneTimeDateChange && (
        <div className="space-y-3">
          <Label variant="section">
            {t('availability.dialog.date', 'Date')}
          </Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  'w-full min-h-[48px] justify-start text-left font-normal',
                  !oneTimeDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {oneTimeDate ? format(oneTimeDate, 'PPP') : t('availability.dialog.pickDate', 'Pick a date')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={oneTimeDate ?? undefined}
                onSelect={(d) => {
                  onOneTimeDateChange(d ?? null);
                  setCalendarOpen(false);
                }}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      <div className="space-y-3">
        <Label variant="section">
          {t('availability.wizard.steps.time', 'Time')}
        </Label>
        <div className="space-y-2">
          <Label>
            {t('availability.dialog.startTime', 'Start Time')}
          </Label>
          <TimePicker value={startTime} onChange={onStartTimeChange} />
        </div>
        <p className="text-sm text-gray-600 flex items-center gap-2">
          <span className="tabular-nums">{startTime}</span>
          <ArrowRight className="w-3 h-3 text-gray-400" />
          <span className="tabular-nums font-medium text-gray-900">{computedEnd}</span>
          <span className="text-gray-400">·</span>
          <span>
            {t('availability.groupClass.endTimeAuto', '{{duration}} min · {{service}}', {
              duration: durationMinutes,
              service: serviceName,
            })}
          </span>
        </p>
      </div>
    </div>
  );
}
